import banner from './TipTagLogo.png';
import logo from './TT_LOGO.png';
import './App.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import { mountEmbeddedPayments, createHostedSession, pickWallet } from './adumo';
import { resolveCallbackReference } from './callbackRef';

/**
 * TipTag pay-in app - Adumo Online (Lesaka) v3 embedded SDK.
 *
 * The SDK renders both payment options in-page: a device-aware wallet tile
 * (Apple Pay when ApplePaySession proves availability, otherwise Google Pay)
 * and a Card tile. The backend preserves the Omnea allocation contract by
 * minting a token and embedding it in Adumo's MerchantReference before any
 * session is returned.
 *
 * Industry layouts (?IndustryId=) and presets are preserved from the original app.
 */

// SDK-only build: wallet + card must stay on the TipTag page. Allocation is
// still token-first: the backend mints an Omnea token and embeds it in Adumo's
// MerchantReference before any SDK session is returned.

// ── helpers ───────────────────────────────────────────────────────────────────
const toCents = (rands) => Math.round(parseFloat(rands || '0') * 100);

const fmtRands = (cents) => {
  const r = cents / 100;
  return r % 1 === 0 ? String(r) : r.toFixed(2);
};

/** Map an Adumo _STATUS (or raw reason) to guest-friendly copy. Ported from the
 *  monorepo PayCard hostedFailureMessage / walletFailureMessage. */
function friendlyFailure(statusOrReason) {
  const s = (statusOrReason || '').toString().trim();
  const code = s.toUpperCase();

  if (code === 'USER_CANCELLED' || code === 'CANCELLED') {
    return 'Payment cancelled. You have not been charged.';
  }
  if (code === 'DECLINED' || code === 'INSUFFICIENT_FUNDS') {
    return 'Your bank declined the payment. You have not been charged. Please try again or use a different card.';
  }
  // Any 3-D Secure / bank-authentication outcome (TDS_AUTH_REQUIRED, TDS_AUTH_FAILED,
  // 3DS…) - the card verification step didn't complete.
  if (code.indexOf('TDS') !== -1 || code.indexOf('3DS') !== -1 || code.indexOf('AUTH') !== -1) {
    return "We couldn't verify your card with your bank. You have not been charged. Please try again or use a different card.";
  }
  if (/OR_BIBED|INVALID ORIGIN|MERCHANT IS HAVING TROUBLE/i.test(s)) {
    return 'Wallet payment is not available on this site yet. Please pay with card.';
  }
  // Everything else (APPROVED-but-not-finalised, blank, or ANY raw gateway code) → one
  // generic message. NEVER surface a raw backend status/variable name to the guest.
  return 'Your payment could not be completed. You have not been charged. Please try again.';
}

// ── Success receipt ───────────────────────────────────────────────────────────
function Receipt({ amountCents, reference, status, paidAt, onReset }) {
  const when = paidAt || new Date();
  const datePart = when.toLocaleString('en-ZA', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
  const timePart = when.toLocaleString('en-ZA', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });

  return (
    <div className="App" style={{ paddingTop: '2rem' }}>
      <img src={banner} className="App-logo" alt="banner" />
      <img src={logo} className="App-header-logo" alt="TT logo" />
      <div className="Receipt" role="status" aria-live="polite">
        <div className="Receipt-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
               strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 className="Receipt-title">Payment Successful</h2>
        <p className="Receipt-amount">R {fmtRands(amountCents)}</p>
        <p className="Receipt-sub">This screen is your proof of payment.</p>

        <dl className="Receipt-details">
          <dt>Date</dt><dd>{datePart}</dd>
          <dt>Time</dt><dd>{timePart}</dd>
          {status ? (<><dt>Status</dt><dd>{status}</dd></>) : null}
          {reference ? (<><dt>Reference</dt><dd className="Receipt-ref">{reference}</dd></>) : null}
        </dl>

        <button type="button" className="Receipt-again" onClick={onReset}>
          Make another payment
        </button>
      </div>
      <img src={banner} className="App-logo-Bottom" alt="logo" />
    </div>
  );
}

// ── Failure screen ────────────────────────────────────────────────────────────
function FailureScreen({ reason, onRetry }) {
  return (
    <div className="App" style={{ paddingTop: '2rem' }}>
      <img src={banner} className="App-logo" alt="banner" />
      <img src={logo} className="App-header-logo" alt="TT logo" />
      <div className="Receipt Receipt--fail" role="alert" aria-live="assertive">
        <div className="Receipt-cross" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
               strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
        <h2 className="Receipt-title">Payment Not Completed</h2>
        <p className="Receipt-sub Receipt-sub--fail">{reason}</p>
        <button type="button" className="Receipt-again" onClick={onRetry}>
          Try again
        </button>
      </div>
      <img src={banner} className="App-logo-Bottom" alt="logo" />
    </div>
  );
}

// ── Payment CTAs. Every method comes from Adumo's SDK. The wallet is device-aware:
//    Apple Pay only when ApplePaySession proves capability, otherwise Google Pay, which
//    the SDK renders as Google's own official button (googlePayStyle 'google') per
//    Google's brand guidelines. Card is an SDK tile too.
//    If the SDK cannot mount, Adumo hosted card opens inside a TipTag iframe/modal only.
//    No payment path navigates the main window away from TipTag. ─────────────────────
function PaymentSection({ tagId, chargeCents, payType, inlineError, onSuccess, onFailure }) {
  const googleButtonRef = useRef(null);
  const applePayRef = useRef(null);
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const lastMintKey = useRef('');
  const [mode, setMode] = useState('idle'); // idle | sdk | fallback | blocked
  const [blockedReason, setBlockedReason] = useState('');
  const [cardFrameUrl, setCardFrameUrl] = useState(null);
  const [walletKind, setWalletKind] = useState('google');
  // No separate wallet-readiness state: the SDK mounts the wallet inside initPayments'
  // onReady, so mode==='sdk' already means the wallet element has been asked to render.
  // Whether it PAINTS is the SDK's own isReadyToPay call, which renders nothing when the
  // device cannot pay - handled in CSS by letting the empty container collapse.
  const paymentReady = mode === 'sdk';

  useEffect(() => {
    lastMintKey.current = '';
    setMode('idle');
    setBlockedReason('');
    setCardFrameUrl(null);
    setWalletKind(pickWallet());
  }, [chargeCents, payType]);

  useEffect(() => {
    if (!tagId || chargeCents <= 0) return undefined;
    const key = `${payType}:${chargeCents}`;
    let cancelled = false;

    const google = googleButtonRef.current;
    const apple = applePayRef.current;
    const card = cardRef.current;
    const payments = paymentsRef.current;
    // Only require the nodes THIS wallet path actually mounts into. The Apple container
    // is rendered solely in the non-Google branch below, so on the Google rail it is
    // legitimately absent - and adumo.js only ever touches applePayRef when
    // pickWallet() === 'apple'. Demanding it here stranded the whole SDK mount (card
    // included) behind a node that never exists on Chrome/Firefox/Edge/Android, leaving
    // the shared loader spinning forever with no reachable payment method.
    if (!card || !payments) return () => { cancelled = true; };
    if (walletKind === 'apple' && !apple) return () => { cancelled = true; };
    if (walletKind === 'google' && !google) return () => { cancelled = true; };
    if (key === lastMintKey.current) return () => { cancelled = true; };

    lastMintKey.current = key;
    setMode('idle');

    const t = setTimeout(() => {
      mountEmbeddedPayments({
        tagId,
        amountCents: chargeCents,
        payType,
        googlePayRef: google,
        applePayRef: apple,
        cardRef: card,
        paymentsRef: payments,
        walletKind,
        onSuccess: (ref) => onSuccess(ref, chargeCents, payType),
        onFailure: (reason) => onFailure(reason),
        onCancel: () => onFailure('USER_CANCELLED'),
      }).then((handle) => {
        if (cancelled) return;
        if (handle.state === 'embedded') {
          setMode('sdk');
          return;
        }
        setBlockedReason(handle.reason || handle.state || 'blocked');
        createHostedSession({ tagId, amountCents: chargeCents, payType }).then((fallback) => {
          if (cancelled) return;
          if (fallback.state === 'ok' && fallback.url) {
            setCardFrameUrl(fallback.url);
            setMode('fallback');
          } else {
            setBlockedReason(fallback.reason || fallback.state || handle.reason || handle.state || 'blocked');
            setMode('blocked');
          }
        });
      });
    }, 10);

    return () => { cancelled = true; clearTimeout(t); };
  }, [tagId, chargeCents, payType, walletKind, onSuccess, onFailure]);

  useEffect(() => {
    const onMsg = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data && data.type === 'PAYMENT_CALLBACK_PAGE') {
        // No pending reference to fall back on: this handler now serves only the hosted
        // card leg, which has no reference until api/return.php sends one, so the
        // callback payload is the sole source. (It used to carry one, captured before
        // the hand-rolled Google Pay rail handed off to a 3DS challenge. The SDK owns
        // 3DS now and reports through its own onSuccess.) No reference means no receipt.
        const ref = resolveCallbackReference(data, '');
        if (ref) onSuccess(ref, chargeCents, payType);
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [chargeCents, payType, onSuccess]);

  // Device-aware wallet: exactly ONE wallet is offered, and only when the device can
  // actually complete it. pickWallet() proves Apple Pay capability (ApplePaySession +
  // canMakePayments); on the Google rail the SDK element runs Google's own isReadyToPay
  // and renders nothing when it fails, so the container collapses to zero height and the
  // "or pay by card" divider is left sitting above the card tile on its own. That reads
  // as a payment option the guest cannot see, so the divider is CSS-hidden in that case
  // rather than conditioned on a readiness flag React does not have.
  const showAppleWallet = walletKind === 'apple';
  const showGoogleWallet = walletKind === 'google';
  const showWallet = showAppleWallet || showGoogleWallet;

  if (!tagId) return <button className="Pay-cta" disabled>Tag not found</button>;
  if (chargeCents <= 0) return <p className="Pay-hint">Enter an amount to pay.</p>;

  return (
    <div className="PaymentSection">
      {inlineError ? <div className="Pay-error" role="alert">{inlineError}</div> : null}

      <p className="Pay-label">Pay R {fmtRands(chargeCents)} with</p>

      <div className={paymentReady ? 'Pay-options' : 'Pay-options Pay-options--preparing'} aria-hidden={!paymentReady}>
        {showAppleWallet && (
          <div ref={applePayRef} className="Pay-wallet" aria-label="Apple Pay wallet" />
        )}
        {showGoogleWallet && (
          <div ref={googleButtonRef} className="Pay-wallet-gpay" aria-label="Google Pay wallet" />
        )}
        {showWallet && <div className="Pay-or"><span>or pay by card</span></div>}
        <div ref={cardRef} className="Pay-card" />
        <div ref={paymentsRef} className="Pay-form" />
      </div>

      {!paymentReady && mode !== 'fallback' && mode !== 'blocked' && (
        <div className="Pay-loading Pay-loading--combined" role="status" aria-live="polite">
          <div className="PaySpinner PaySpinner--sm" aria-hidden="true" />
          <span className="Pay-loading-label">Loading secure payment options…</span>
        </div>
      )}

      {mode === 'fallback' && cardFrameUrl && (
        <div className="PayModal" role="dialog" aria-modal="true" aria-label="Secure payment">
          <div className="PayModal-backdrop" />
          <div className="PayModal-panel">
            <button className="PayModal-close" type="button" onClick={() => { setCardFrameUrl(null); setMode('blocked'); }}>×</button>
            <div className="PayModal-head">
              <strong>Complete payment securely</strong>
              <span>Still on TipTag. Do not close this until the receipt appears.</span>
            </div>
            <iframe className="Pay-cardframe" title="Secure payment" src={cardFrameUrl} />
          </div>
        </div>
      )}

      {mode === 'blocked' && (
        <div className="Pay-error" role="status">
          <p style={{ margin: 0 }}>In-page payment isn't available on this session yet.</p>
          <p style={{ margin: '4px 0 0', fontSize: '.82em', opacity: 0.75 }}>
            The page stayed here and no off-page redirect was used ({blockedReason}).
          </p>
        </div>
      )}

      <p className="Pay-trust">Secure payment · POPIA-compliant</p>
    </div>
  );
}

function App() {
  const [amount, setAmount] = useState('');
  const [percent, setPercent] = useState('');
  const [message, setMessage] = useState('');      // transient validation hints
  const [inlineError, setInlineError] = useState(''); // SDK-path payment error (kept on the form)
  const [tagId, setTagId] = useState('');
  const [IndustryId, setIndustry] = useState('');
  const [isWaiter, setIsWaiter] = useState(true);
  const [includeCharges, setIncludeCharges] = useState(false); // customer covers the transaction fee

  // Screen model: 'pay' | 'success' | 'failed'.
  const [screen, setScreen] = useState('pay');
  const [receipt, setReceipt] = useState(null);   // { amountCents, reference, status, paidAt }
  const [failReason, setFailReason] = useState('');

  // Base tip in rands: percent industries tip a % of the bill, others pay the entered amount.
  const isPercent = IndustryId === '2';
  const baseTipRaw = isPercent
    ? (parseFloat(amount || '0') * parseFloat(percent || '0')) / 100
    : parseFloat(amount || '0');
  const baseTip = Number.isFinite(baseTipRaw) && baseTipRaw > 0 ? baseTipRaw : 0;
  // "Cover the transaction fee" → gross up so the recipient nets the full tip (mirrors the
  // live getServiceFee math: base / 0.976667 / 0.96). feeRands is the difference shown to the guest.
  const grossTip = baseTip > 0 ? parseFloat(((baseTip / 0.976667) / 0.96).toFixed(2)) : 0;
  const feeRands = parseFloat((grossTip - baseTip).toFixed(2));
  const chargeCents = toCents((includeCharges ? grossTip : baseTip).toFixed(2));

  // Auto-clear transient validation messages (NOT payment errors).
  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  // Parse tagId + IndustryId + any legacy return params from the URL once.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);

    const tagIdFromUrl = q.get('tagId');
    setTagId(tagIdFromUrl || '');

    // IndustryId - remember it per tag so it SURVIVES the payment-return redirect
    // (return.php drops it from the URL). Restore from storage when the URL lacks it,
    // otherwise after paying we'd fall out of golf mode and lose the round/tip flow.
    let industry = '';
    const industryFromUrl = q.get('IndustryId');
    if (industryFromUrl && !isNaN(industryFromUrl)) {
      industry = industryFromUrl;
      try { if (tagIdFromUrl) localStorage.setItem(`tiptag_industry_${tagIdFromUrl}`, industry); } catch (e) { /* ignore */ }
    } else {
      try { industry = (tagIdFromUrl && localStorage.getItem(`tiptag_industry_${tagIdFromUrl}`)) || ''; } catch (e) { industry = ''; }
    }
    setIndustry(industry);

    // Legacy return parser retained so old callback URLs fail safely. The SDK-only
    // success path renders receipts from SDK onSuccess without navigation.
    const paid = q.get('paid');

    if (paid === '1') {
      setReceipt({
        amountCents: parseInt(q.get('pamt') || '0', 10),
        reference: q.get('pref') || '',
        status: q.get('pstatus') || 'APPROVED',
        paidAt: new Date(),
      });
      setScreen('success');
    } else if (paid === '0') {
      setFailReason(friendlyFailure(q.get('pstatus') || ''));
      setScreen('failed');
    }
    // Strip return params so a refresh shows the normal pay page - but KEEP tagId +
    // IndustryId so we stay in the right industry (golf) flow after paying.
    if (paid !== null && window.history && window.history.replaceState) {
      const keep = tagIdFromUrl
        ? `?tagId=${encodeURIComponent(tagIdFromUrl)}${industry ? `&IndustryId=${industry}` : ''}`
        : '';
      window.history.replaceState({}, '', `/${keep}`);
    }
  }, []);

  // Hosted iframe fallback completion - return.php postMessages the parent with a
  // same-origin receipt target. Navigate the SPA shell only, not the browser to Adumo.
  useEffect(() => {
    const onMsg = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== 'tiptag-pay-return' || typeof data.target !== 'string') return;
      const url = new URL(data.target, window.location.origin);
      const q = url.searchParams;
      const paid = q.get('paid');
      if (paid === '1') {
        setReceipt({
          amountCents: parseInt(q.get('pamt') || '0', 10),
          reference: q.get('pref') || '',
          status: q.get('pstatus') || 'APPROVED',
          paidAt: new Date(),
        });
        setInlineError('');
        setScreen('success');
      } else if (paid === '0') {
        setFailReason(friendlyFailure(q.get('pstatus') || ''));
        setScreen('failed');
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // SDK-path success - Adumo's onSuccess fires IN-PAGE; render the receipt on the main
  // page directly from the transaction reference (no redirect, no return.php).
  const handleSuccess = useCallback((ref, paidCents) => {
    setReceipt({ amountCents: paidCents, reference: ref || '', status: 'APPROVED', paidAt: new Date() });
    setInlineError('');
    setScreen('success');
  }, []);

  // SDK-path failure / cancel - keep the guest on the form (amount preserved) so they
  // can retry; show a friendly inline message.
  const handleFailure = useCallback((reason) => {
    setInlineError(friendlyFailure(reason));
  }, []);

  const resetForAnother = () => {
    setScreen('pay');
    setReceipt(null);
    setFailReason('');
    setInlineError('');
    setMessage('');
    setAmount('');
    setPercent('');
  };

  // ── Success / failure screens ────────────────────────────────────────────────
  if (screen === 'success' && receipt) {
    return (
      <Receipt
        amountCents={receipt.amountCents}
        reference={receipt.reference}
        status={receipt.status}
        paidAt={receipt.paidAt}
        onReset={resetForAnother}
      />
    );
  }
  if (screen === 'failed') {
    return <FailureScreen reason={failReason} onRetry={resetForAnother} />;
  }

  // Shared payment block - rendered under each industry's amount selector. The checkbox
  // grosses up the charge so the recipient nets the full tip; our two buttons (wallet +
  // card) then charge whichever amount is shown.
  const paySection = (
    <>
      {chargeCents > 0 && (
        <label className="Pay-includefee">
          <input
            type="checkbox"
            checked={includeCharges}
            onChange={(e) => setIncludeCharges(e.target.checked)}
          />
          <span>Cover the transaction fee{feeRands > 0 ? ` (+R${feeRands.toFixed(2)})` : ''}</span>
        </label>
      )}
      <PaymentSection
        tagId={tagId}
        chargeCents={chargeCents}
        payType="tip"
        inlineError={inlineError}
        onSuccess={handleSuccess}
        onFailure={handleFailure}
      />
    </>
  );

  // The lower bound is enforced on BLUR, not per keystroke. The field is controlled
  // (value={amount}) so a preset tap shows up in it - but that also means rewriting the
  // value mid-typing corrupts everything typed after the rewrite: entering "0.50" snaps
  // the leading "0" to "1", and the remaining keystrokes then build "1.50" - a different
  // amount than the guest chose, changed under them without a word. The upper bound stays
  // live because clamping at the top cannot swallow later digits.
  const clampAmountOnBlur = () => {
    if (amount === '') return;
    const num = Number(amount);
    if (!Number.isFinite(num)) { setAmount(''); return; }
    if (num < 1) { setAmount('1'); return; }
    if (num > 1000000) { setAmount('1000000'); }
  };

  const customAmountInput = (placeholder) => (
    <input
      type="number" min="1" step="0.01" max="1000000" placeholder={placeholder}
      value={amount}
      onChange={(e) => {
        const val = e.target.value;
        if (val === '') return setAmount('');
        const num = Number(val);
        if (Number.isNaN(num)) return;
        if (num > 1000000) return setAmount('1000000');
        setAmount(val);
      }}
      onBlur={clampAmountOnBlur}
    />
  );

  const messageBlock = message ? <div className="Message">{message}</div> : null;

  // ── Industry 1 - Golf Clubs (caddie tip) ─────────────────────────────────────
  if (IndustryId === '1') {
    return (
      <div className="App" style={{ paddingTop: '3rem' }}>
        <img src={banner} className="App-logo" alt="banner" />
        <img src={logo} className="App-header-logo" alt="TT logo" />
        <div className="TipButtonsUnique">
          {['10', '15', '20', '50', '100', '200'].map((v) => (
            <button key={v} onClick={() => setAmount(v)}>R{v}</button>
          ))}
        </div>
        <div className="CustomAmount">
          {customAmountInput('Enter Tip Amount')}
          {paySection}
          {messageBlock}
        </div>
        <img src={banner} className="App-logo-Bottom" alt="logo" />
      </div>
    );
  }

  // ── Industry 2 - Waiter / Bartender (percentage of bill) ─────────────────────
  if (IndustryId === '2') {
    return (
      <div className="App" style={{ paddingTop: '0rem' }}>
        <img src={logo} className="App-header-logo" alt="TT logo" />
        <div>
          <button
            onClick={() => setIsWaiter(!isWaiter)}
            style={{
              padding: '0.5rem 1rem', fontSize: '1.5rem', margin: '-10px auto 0px',
              backgroundColor: isWaiter ? '#c8df00' : '#a1a1a139', color: 'white',
              borderRadius: '4px', borderColor: isWaiter ? '#a1a1a139' : '#c8df00', cursor: 'pointer',
            }}
          >
            {isWaiter ? 'Waiter' : 'Bartender'}
          </button>
        </div>
        <div className="TipButtonsUnique" style={{ backgroundImage: `url(${require(isWaiter ? './TipTagWaiter.png' : './TipTagBarTender.png')})` }}>
          {['5', '10', '15', '20', '25', '30'].map((p) => (
            <button key={p} className={percent === p ? 'active' : ''} onClick={() => setPercent(p)}>{p}%</button>
          ))}
        </div>
        <div className="CustomAmount">
          {/* Was a verbatim copy of customAmountInput's markup + handler, so every fix
              had to be made twice (and the clamp fix above would have missed this one). */}
          {customAmountInput('Enter Bill Amount')}
          {paySection}
          {messageBlock}
        </div>
        <img src={banner} className="App-logo-Bottom" alt="logo" />
      </div>
    );
  }

  // ── Industry 3 - Delivery Drivers / Industry 4 - Car Guards (same layout) ─────
  if (IndustryId === '3' || IndustryId === '4') {
    // NOTE: require() must take STRING LITERALS (not a variable) - a bare
    // `require(`${bg}`)` makes webpack build a broad require-context over src/
    // that sweeps in the test files and breaks `react-scripts build` with
    // "'act' is not exported from 'react'". Keep the literals inline.
    return (
      <div className="App" style={{ paddingTop: '3rem' }}>
        <img src={logo} className="App-header-logo" alt="TT logo" />
        <div className="TipButtonsUnique" style={{ backgroundImage: `url(${require(IndustryId === '3' ? './TipTagDriver.png' : './TipTagCarGuard.png')})` }}>
          {['10', '15', '20', '50', '100', '200'].map((v) => (
            <button key={v} onClick={() => setAmount(v)}>R{v}</button>
          ))}
        </div>
        <div className="CustomAmount">
          {customAmountInput('Enter Custom Amount')}
          {paySection}
          {messageBlock}
        </div>
        <img src={banner} className="App-logo-Bottom" alt="logo" />
      </div>
    );
  }

  // ── Default - no/unknown industry ────────────────────────────────────────────
  return (
    <div className="App">
      <img src={banner} className="App-logo" alt="banner" />
      <img src={logo} className="App-header-logo" alt="TT logo" />
      <div className="TipButtons">
        {['10', '15', '20', '50', '100', '200'].map((v) => (
          <button key={v} onClick={() => setAmount(v)}>R{v}</button>
        ))}
      </div>
      <div className="CustomAmount">
        {customAmountInput('Enter Custom Amount')}
        {paySection}
        {messageBlock}
      </div>
      <img src={banner} className="App-logo-Bottom" alt="logo" />
    </div>
  );
}

export default App;
